import { EventItem, Offer, PageConfig, PaymentMode } from '@tixa/schema'
import { AnalyticsEventsHandler } from './types'

import { isFee, isShippingOffer, hasInCart } from './utils'

const HUBSPOT_SCRIPT_ELEMENT_ID = 'hs-script-loader'

const hubSpotLoaderUrl = (): string | undefined => {
  const url = import.meta.env.VITE_HUBSPOT_LOADER_URL?.trim()
  return url || undefined
}

const getPaymentTypeLabel = (mode: PaymentMode | undefined): string => {
  if (mode == null) return 'Credit Card'
  const labels: Record<PaymentMode, string> = {
    [PaymentMode.accountcredit]: 'Account Credit',
    [PaymentMode.card]: 'Credit Card',
    [PaymentMode.mkbszep]: 'MKB SZÉP',
    [PaymentMode.otpszep]: 'OTP SZÉP',
    [PaymentMode.knhszep]: 'K&H SZÉP',
    [PaymentMode.otopszepszallas]: 'OTP SZÉP Szállás',
    [PaymentMode.mkbszepszallas]: 'MKB SZÉP Szállás',
    [PaymentMode.free]: 'Free',
  }
  return labels[mode] ?? mode
}

const createCartTotals = (offers: Offer[]) => {
  const shipping = offers
    .filter(isShippingOffer)
    .reduce((prev, curr) => prev + curr.price, 0)
  const tax = offers.filter(isFee).reduce((prev, curr) => prev + curr.price, 0)
  const value = offers
    .filter(hasInCart)
    .reduce(
      (prev, curr) => prev + curr.price * (curr.eligibleQuantity?.value ?? 1),
      0
    )
  const coupon = offers
    .filter((offer) => offer.appliedCoupon !== undefined)
    .map((offer) => offer.appliedCoupon)
    .reduce((prev, curr) => `${prev}${prev ? ' / ' : ''}${curr}`, '')
  return { shipping, tax, value, coupon: coupon || undefined }
}

const convertOffersToCheckoutItems = (offers: Offer[]) =>
  offers.filter(hasInCart).map((offer) => {
    const descriptionValue = Array.isArray(offer.description)
      ? offer.description[0]
      : offer.description
    return {
      item_id: offer.sku,
      item_name: offer.name,
      price: offer.price,
      item_brand: offer.name,
      item_category: descriptionValue,
      item_variant: offer.category || 'ticket',
      quantity: offer.eligibleQuantity?.value ?? 0,
    }
  })

const convertOffersForViewList = (offers: Offer[]) =>
  offers.map((offer) => {
    const descriptionValue = Array.isArray(offer.description)
      ? offer.description[0]
      : offer.description
    return {
      item_id: offer.sku,
      item_name: offer['@id'],
      item_category: descriptionValue,
      quantity: offer.eligibleQuantity.value,
      price: offer.price,
      currency: offer.priceCurrency,
      item_variant: offer.category,
      coupon: offer.appliedCoupon,
    }
  })

const convertSearchEventsToItems = (events: EventItem[]) =>
  events.map((ev, i) => ({
    item_id: ev.identifier,
    item_name: ev.name,
    item_brand: ev.location?.name,
    index: i + 1,
  }))

const serpItem = (ev: EventItem, index: number) => ({
  item_id: ev.identifier,
  item_name: ev.name,
  item_brand: ev.location?.name,
  index,
})

const scalarProps = (
  input: Record<string, unknown>
): Record<string, string | number | boolean> => {
  const out: Record<string, string | number | boolean> = {}
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined || v === null) continue
    if (
      typeof v === 'string' ||
      typeof v === 'number' ||
      typeof v === 'boolean'
    )
      out[k] = v
    else out[k] = JSON.stringify(v)
  }
  return out
}

const ensureHsq = (): HubSpotQueue => {
  window._hsq = window._hsq ?? []
  return window._hsq
}

const injectHubSpotLoaderOnce = (loaderUrl: string): void => {
  if (document.getElementById(HUBSPOT_SCRIPT_ELEMENT_ID)) return
  const script = document.createElement('script')
  script.type = 'text/javascript'
  script.id = HUBSPOT_SCRIPT_ELEMENT_ID
  script.async = true
  script.defer = true
  script.src = loaderUrl
  document.head.append(script)
}

const createHubSpotAnalyticsHandler = (): AnalyticsEventsHandler => {
  let isActive = false
  let loaderRequested = false

  const checkoutStartTime = new Date()
  const paymentStartTime = new Date()
  let queueStartTime = new Date()

  const getFinishTime = (startTime: Date) =>
    Math.round((new Date().getTime() - startTime.getTime()) / 1000)

  const track = (
    name: string,
    properties?: Record<string, string | number | boolean | unknown>
  ) => {
    if (!isActive) return
    const hsq = ensureHsq()
    const payload: HubSpotCustomBehavioralEvent = {
      name,
      properties: properties
        ? scalarProps(properties as Record<string, unknown>)
        : {},
    }
    hsq.push(['trackCustomBehavioralEvent', payload])
  }

  return {
    init: async (_config: PageConfig) => {
      void _config
      if (loaderRequested) return
      loaderRequested = true
      const url = hubSpotLoaderUrl()
      if (!url) return
      ensureHsq()
      injectHubSpotLoaderOnce(url)
      isActive = true
    },

    searched: (term: string) => track('tixa_search', { search_term: term }),

    viewSearchResultList: (term: string, events: EventItem[]) => {
      const items = convertSearchEventsToItems(events)
      track('tixa_view_item_list', {
        context: 'serp',
        search_term: term || 'serp',
        item_count: events.length,
        items_json: JSON.stringify(items),
      })
    },

    selectSearchResultItem: (term: string, event: EventItem, index: number) => {
      track('tixa_select_item', {
        context: 'serp',
        search_term: term || 'serp',
        index,
        items_json: JSON.stringify([serpItem(event, index)]),
      })
    },

    viewed: (offers: Offer[]) =>
      track('tixa_view_item_list', {
        context: 'ticket_list',
        item_count: offers.length,
        items_json: JSON.stringify(convertOffersForViewList(offers)),
      }),

    addedToCart: (params: {
      name: string
      offerId: string
      ticketTypeName: string
      ticketTypeId: string
      offerPrice: number
      currency: string
      quantity: number
      coupon?: string
      type?: string
    }) => {
      track('add_to_cart', {
        item_id: params.ticketTypeId,
        item_name: params.name,
        price: params.offerPrice,
        item_category: params.ticketTypeName,
        item_variant: params.type || 'ticket',
        quantity: params.quantity,
        currency: params.currency,
        offer_id: params.offerId,
        ...(params.coupon ? { coupon: params.coupon } : {}),
      })
    },

    removedFromCart: (params: {
      name: string
      offerId: string
      ticketTypeName: string
      ticketTypeId: string
      offerPrice: number
      currency: string
      quantity: number
      type?: string
    }) => {
      track('remove_from_cart', {
        item_id: params.ticketTypeId,
        item_name: params.name,
        price: params.offerPrice,
        item_category: params.ticketTypeName,
        item_variant: params.type || 'ticket',
        quantity: params.quantity,
        currency: params.currency,
        offer_id: params.offerId,
      })
    },

    selectPromotion: (params: {
      promotion_id: string
      promotion_name: string
      creative_name?: string
      creative_slot?: string
    }) => {
      track('tixa_select_promotion', {
        promotion_id: params.promotion_id,
        promotion_name: params.promotion_name,
        creative_name: params.creative_name ?? '',
        creative_slot: params.creative_slot ?? 'Homepage Banner',
      })
    },

    checkoutStarted: (cartId: string, offers: Offer[]) => {
      const items = convertOffersToCheckoutItems(offers)
      track('begin_checkout', {
        cart_id: cartId,
        item_count: items.length,
        items_json: JSON.stringify(items),
      })
    },

    checkoutExpired: () =>
      track('cart-expired', {
        event_category: 'cart',
        event_label: 'CartExpired',
        value: getFinishTime(checkoutStartTime),
      }),

    paymentInfoAdded: (
      offers: Offer[],
      _data?: unknown,
      paymentMode?: PaymentMode
    ) => {
      const items = convertOffersToCheckoutItems(offers).map((i) => ({
        item_id: i.item_id,
        item_name: i.item_name,
        quantity: i.quantity,
      }))
      track('add_payment_info', {
        payment_type: getPaymentTypeLabel(paymentMode),
        item_count: items.length,
        items_json: JSON.stringify(items),
      })
    },

    formInvalid: () =>
      track('cart-form-invalid', {
        event_category: 'cart',
        event_label: 'Form is invalid',
      }),

    appliedCoupon: () =>
      track('applied-coupon', {
        event_category: 'event-box',
        event_label: 'Applied Coupon',
      }),

    appliedCode: () =>
      track('applied-code', {
        event_category: 'event-box',
        event_label: 'Applied Code',
      }),

    openedSeatmap: () =>
      track('opened-seatmap', {
        event_category: 'event-box',
        event_label: 'Opened Seatmap',
      }),

    paymentStarted: () =>
      track('cart-payment-started', {
        event_category: 'cart',
        event_label: 'Payment started',
        value: getFinishTime(checkoutStartTime),
      }),

    paymentFailure: () =>
      track('cart-payment-failure', {
        event_category: 'cart',
        event_label: 'Payment failure',
        value: getFinishTime(paymentStartTime),
      }),

    purchased: (id: string, offers: Offer[]) => {
      const totals = createCartTotals(offers)
      const items = convertOffersToCheckoutItems(offers)
      track('purchase', {
        transaction_id: id,
        affiliation: 'TIXA.hu',
        value: totals.value,
        tax: totals.tax,
        shipping: totals.shipping,
        currency: offers[0]?.priceCurrency ?? 'HUF',
        item_count: items.length,
        items_json: JSON.stringify(items),
        ...(totals.coupon ? { coupon: totals.coupon } : {}),
      })
    },

    registered: (value: number, currency: string) =>
      track('cart-registered', {
        event_category: 'cart',
        event_label: 'Registered in cart',
        value,
        currency,
      }),

    closePage: () =>
      track('cart-closed-page', {
        event_category: 'cart',
        event_label: 'Closed page in cart',
      }),

    gotIntoQueue: () => {
      queueStartTime = new Date()
      track('queue-start', {
        event_category: 'queue',
        event_label: 'Start queue',
      })
    },

    leftQueue: () =>
      track('queue-left', {
        event_category: 'queue',
        event_label: 'Left queue',
        value: getFinishTime(queueStartTime),
      }),

    finishQueue: () =>
      track('queue-finished', {
        event_category: 'queue',
        event_label: 'Queue finished',
        value: getFinishTime(queueStartTime),
      }),

    queueSoldout: () =>
      track('queue-soldout', {
        event_category: 'queue',
        event_label: 'Queue sold out',
        value: getFinishTime(queueStartTime),
      }),
  }
}

export { createHubSpotAnalyticsHandler }

type HubSpotCustomBehavioralEvent = {
  name: string
  properties?: Record<string, string | number | boolean>
}

type HubSpotQueue = Array<
  ['trackCustomBehavioralEvent', HubSpotCustomBehavioralEvent] | unknown[]
>
